import { Injectable } from '@angular/core';
import { GlobalMessageService } from '../../../global-services/global-message.service';
import { GlobalLoadingService } from '../../../global-services/global-loading.service';
import { FirebaseService } from '../../../global-services/firebase.service';
import { Router } from '@angular/router';
import { getAuth, User } from 'firebase/auth';
import { GuardHelper } from '../../../global-guards/guard.helper';

@Injectable({ providedIn: 'root' })
export class ConfirmEmailFacade {
  private checkInterval?: NodeJS.Timeout;

  constructor(
    private messageService: GlobalMessageService,
    private loadingService: GlobalLoadingService,
    private firebaseService: FirebaseService,
    private router: Router,
  ) { }

  async start(): Promise<string> {
    try {
      this.loadingService.show();
      const auth = await GuardHelper.getCurrentUser();

      if (auth === null) {
        throw new Error('User not authenticated');
      }

      // Start listening for email verification changes
      this.startListeningForEmailVerification();

      return auth!.email!;
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'unknown error';
      this.messageService.showError('Error', `An error occurred: ${message}`);
      return '';
    } finally {
      this.loadingService.hide();
    }
  }

  private startListeningForEmailVerification(): void {
    console.log('Starting to listen for email verification changes...');

    // Check periodically every 5 seconds
    this.checkInterval = setInterval(async () => {
      try {
        const user = await GuardHelper.getCurrentUser();
        if (user) {
          await user.reload();
          if (user.emailVerified) {
            console.log('Email verified via periodic check!');
            this.handleEmailVerified();
          }
        }
      } catch (error) {
        console.error('Error during periodic verification check:', error);
      }
    }, 3000);
  }

  private async handleEmailVerified(): Promise<void> {
    // Immediately cancel all subscriptions and intervals
    this.destroy();

    try {
      this.messageService.showSuccess('Email Verified', 'Your email has been successfully verified!');
      this.router.navigate(['/home']);
    } catch (error) {
      console.error('Error handling email verification:', error);
    }
  }

  async check() {
    try {
      this.loadingService.show();
      const user = await GuardHelper.getCurrentUser();
      await user!.reload();

      const verified = user?.emailVerified;

      if (verified === false) {
        throw new Error('Email not verified, check your inbox');
      }

      this.handleEmailVerified();
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'unknown error';
      this.messageService.showError('Error', `An error occurred: ${message}`);
    } finally {
      this.loadingService.hide();
    }
  }

  async resend() {
    this.loadingService.show();
    this.firebaseService.sendVerificationEmail();
    this.messageService.showSuccess('Confirmation Email Sent', 'A new confirmation email has been sent to your inbox.');
    this.loadingService.hide();
  }

  async signOut() {
    this.loadingService.show();
    await this.firebaseService.signOut();
    this.messageService.showSuccess('Sign Out Successful', 'You have been signed out successfully.');
    this.loadingService.hide();
    this.router.navigate(['/sign-in']);
  }

  destroy(): void {
    // Clear the periodic check interval
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }
}
